import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react'

import { useClickOutside, useEscapeKey } from '@/hooks/useDismiss'
import { cn } from '@/lib/utils'

export type DropdownMenuProps = {
  /** The button that opens the menu. Receives aria + ref wiring. */
  trigger: ReactElement<{
    ref?: Ref<HTMLButtonElement>
    'aria-haspopup'?: string
    'aria-expanded'?: boolean
    'aria-controls'?: string
    onClick?: (event: ReactMouseEvent<HTMLButtonElement>) => void
  }>
  children: ReactNode
  align?: 'start' | 'end'
  /** Accessible name for the menu itself. */
  label: string
  className?: string
}

/**
 * WAI-ARIA menu button. Handles: click/Escape to close, click-outside,
 * ArrowDown/ArrowUp roving focus, Home/End, and focus returning to the trigger.
 * Radix would do the same thing — this is 90 lines and one less dependency.
 */
export function DropdownMenu({
  trigger,
  children,
  align = 'end',
  label,
  className,
}: DropdownMenuProps): ReactNode {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuId = useId()

  const close = useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  useEscapeKey(open, close)
  useClickOutside([menuRef, triggerRef], open, () => setOpen(false))

  // Focus the first item when the menu opens.
  useEffect(() => {
    if (!open) return
    const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])')
    first?.focus()
  }, [open])

  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [],
    )
    if (items.length === 0) return
    const index = items.indexOf(document.activeElement as HTMLElement)

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const delta = event.key === 'ArrowDown' ? 1 : -1
      const next = items[(index + delta + items.length) % items.length]
      next?.focus()
    } else if (event.key === 'Home') {
      event.preventDefault()
      items[0]?.focus()
    } else if (event.key === 'End') {
      event.preventDefault()
      items[items.length - 1]?.focus()
    } else if (event.key === 'Tab') {
      setOpen(false)
    }
  }

  return (
    <div className="relative inline-flex">
      {cloneElement(trigger, {
        ref: triggerRef,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? menuId : undefined,
        onClick: (event: ReactMouseEvent<HTMLButtonElement>) => {
          trigger.props.onClick?.(event)
          setOpen((value) => !value)
        },
      })}

      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          className={cn(
            'animate-menu-in absolute top-[calc(100%+0.375rem)] z-40 min-w-52 overflow-hidden',
            'rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-2',
            align === 'end' ? 'right-0' : 'left-0',
            className,
          )}
        >
          {children}
        </div>
      ) : null}
    </div>
  )
}

export type DropdownItemProps = {
  onSelect?: () => void
  disabled?: boolean
  icon?: ReactNode
  children: ReactNode
  /** Right-aligned hint, e.g. a shortcut or a current value. */
  hint?: ReactNode
  variant?: 'default' | 'destructive'
  /** Marks the item as the active choice in a radio-style group. */
  selected?: boolean
}

export function DropdownItem({
  onSelect,
  disabled = false,
  icon,
  children,
  hint,
  variant = 'default',
  selected,
}: DropdownItemProps): ReactNode {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-left text-body-sm',
        'transition-colors duration-hover disabled:pointer-events-none disabled:opacity-50',
        '[&_svg]:size-4 [&_svg]:shrink-0',
        variant === 'destructive'
          ? 'text-destructive hover:bg-destructive-subtle'
          : 'text-foreground hover:bg-muted',
        selected && 'bg-muted',
      )}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {hint ? <span className="text-caption text-muted-foreground">{hint}</span> : null}
    </button>
  )
}

export function DropdownLabel({ children }: { children: ReactNode }): ReactNode {
  return (
    <p className="px-2.5 pb-1 pt-2 text-overline uppercase text-muted-foreground">{children}</p>
  )
}

export function DropdownSeparator(): ReactNode {
  return <div role="separator" className="my-1 h-px bg-border" />
}
